import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Popover from '@material-ui/core/Popover'
import MenuList from '@material-ui/core/MenuList'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import {
  Check,
  ChevronDown,
  ExternalLink,
  MapPin,
  Tag,
  X,
  COACH_PROFILE_FILTER_ICON_PROPS,
} from '@lib/pga-coach/coach-profile-icons'
import { COACH_PROFILE_OFFERING_FORMAT_ICON_COMPONENTS } from '@lib/pga-coach/coach-profile-offering-format-icons'
import ButtonCta from '@components/atoms/button-cta'
import {
  COACH_PROFILE_OFFERING_SERVICES,
  buildCoachProfileFacilityFilterOptions,
  buildCoachProfileTagFilterOptions,
  formatCoachProfileFacilityFilterOptionLabel,
  formatCoachProfileTagFilterOptionLabel,
  getCoachProfileOfferingServiceId,
  offeringMatchesFacilityFilter,
  offeringMatchesShopFilter,
  offeringMatchesTagFilter,
  sortCoachProfileOfferingsByAvailability,
} from '@lib/pga-coach/offering-search'
import { PGA_COACH_PRIVATE_LISTING_IMAGE } from '@lib/pga-coach/constants'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import CoachProfileOfferingCard from '@components/coach-bio/coach-profile-offering-card'
import { useTrackCoachProfileEvent } from '@lib/pga-coach/coach-profile-analytics'
import useStyles from './coach-profile-offerings-shop.styles'

const OFFERINGS_SHOP_HEADING = 'Services'
const EXTERNAL_BOOKING_IMAGE_WIDTH = 440

export const EXTERNAL_SERVICES_HEADING = 'External Booking Site'
export const EXTERNAL_SERVICES_COPY = 'This coach lists their offerings on a separate website.'
export const EXTERNAL_SERVICES_CTA = 'Open Site'
export const FILTER_EMPTY_HEADING = 'No services match your filters'
export const FILTER_EMPTY_BODY = 'Clear your filters to see this coach\'s services again.'
export const FILTER_EMPTY_CTA = 'Clear all filters'

function OfferingSection({
  label, offerings, sectionKey, hideHeading,
}) {
  const classes = useStyles()

  return (
    <Box data-testid={`coach-profile-shop-section-${sectionKey}`}>
      {!hideHeading && (
        <Box className={classes.sectionHeader}>
          <Typography component="h3" variant="subtitle1" className={classes.sectionLabel}>
            {label}
          </Typography>
        </Box>
      )}
      <Box className={classes.cardList}>
        {offerings.map((offering, index) => (
          <CoachProfileOfferingCard
            key={offering.objectID}
            offering={offering}
            variant="row"
            section={sectionKey}
            position={index + 1}
          />
        ))}
      </Box>
    </Box>
  )
}

OfferingSection.propTypes = {
  label: PropTypes.string.isRequired,
  offerings: PropTypes.array.isRequired,
  sectionKey: PropTypes.string.isRequired,
  hideHeading: PropTypes.bool,
}

OfferingSection.defaultProps = {
  hideHeading: false,
}

function ExternalBookingCard({ externalBookingUrl, className }) {
  const classes = useStyles()
  const trackCoachProfileEvent = useTrackCoachProfileEvent()

  return (
    <Box
      className={className}
      data-testid="coach-profile-external-services"
    >
      <Box className={classes.externalBanner}>
        <Box className={classes.externalBannerImageWrap}>
          <img
            className={classes.externalBannerImage}
            src={composeOptimizedImageUrl(PGA_COACH_PRIVATE_LISTING_IMAGE, EXTERNAL_BOOKING_IMAGE_WIDTH)}
            alt=""
          />
        </Box>
        <Box className={classes.externalBannerBody}>
          <Typography
            component="h3"
            variant="h6"
            className={classes.externalBannerTitle}
          >
            {EXTERNAL_SERVICES_HEADING}
          </Typography>
          <Typography component="p" variant="body2" className={classes.externalBannerCopy}>
            {EXTERNAL_SERVICES_COPY}
          </Typography>
          <ButtonCta
            variant="contained"
            color="primary"
            externalLink={externalBookingUrl}
            onClick={() => trackCoachProfileEvent('click-coach-profile-external-booking', {
              href: externalBookingUrl,
            })}
            endIcon={<ExternalLink {...COACH_PROFILE_FILTER_ICON_PROPS} />}
            dataAttrs={{
              'data-gtm-button-name': 'view-services-external',
            }}
          >
            {EXTERNAL_SERVICES_CTA}
          </ButtonCta>
        </Box>
      </Box>
    </Box>
  )
}

ExternalBookingCard.propTypes = {
  externalBookingUrl: PropTypes.string.isRequired,
  className: PropTypes.string,
}

ExternalBookingCard.defaultProps = {
  className: undefined,
}

function FilterMenuOption({
  label, selected, onSelect,
}) {
  const classes = useStyles()
  return (
    <MenuItem
      selected={selected}
      onClick={onSelect}
      className={classes.filterMenuItem}
      classes={{ selected: classes.filterMenuItemSelected }}
    >
      <ListItemIcon className={classes.filterMenuLeading}>
        {selected
          ? <Check {...COACH_PROFILE_FILTER_ICON_PROPS} data-testid="filter-menu-check" />
          : <span className={classes.filterMenuLeadingSpacer} aria-hidden />}
      </ListItemIcon>
      <Typography component="span" variant="body2">{label}</Typography>
    </MenuItem>
  )
}

FilterMenuOption.propTypes = {
  label: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
}

FilterMenuOption.defaultProps = {
  selected: false,
}

/**
 * Menu of a coach's bookable offerings. Lesson-type pills, Tags,
 * and Location sit on one `/coaches/search`-style row. Type pills mount
 * only when that category has offerings. Tags (multi-select,
 * Tag name (N)) mounts when two or more unique listing tags exist.
 * Location (multi-select, Name / City / ST (N)) mounts when the shop
 * has two or more unique venues. The shop does not render an Audience
 * filter. Circular Clear filters resets type, location, and tags.
 * Combined filters that match no offerings keep the filter row and
 * show a no-matches message with Clear all filters. When the only
 * filter control is a single lesson-type pill (no Tags or Location
 * menus), the whole filter row is omitted because applying it would
 * not change the listed offerings.
 * When `externalBookingUrl` is set (Coach Tools External Tool), the
 * shop shows a photo card (External Booking Site / Open Site) above
 * any PGA Coach listing rows (for example PGA Jr. League programs).
 */
function CoachProfileOfferingsShop({
  offerings = [],
  loading = false,
  id,
  externalBookingUrl = null,
}) {
  const classes = useStyles()
  const trackCoachProfileEvent = useTrackCoachProfileEvent()
  const [serviceId, setServiceId] = useState('all')
  const [facilityKeys, setFacilityKeys] = useState([])
  const [tagKeys, setTagKeys] = useState([])
  const [locationAnchorEl, setLocationAnchorEl] = useState(null)
  const [tagAnchorEl, setTagAnchorEl] = useState(null)

  const availableServices = useMemo(() => (
    COACH_PROFILE_OFFERING_SERVICES.filter((service) => (
      offerings.some((offering) => offeringMatchesShopFilter(offering, service.id))
    ))
  ), [offerings])

  const facilityOptions = useMemo(
    () => buildCoachProfileFacilityFilterOptions(offerings),
    [offerings],
  )

  const tagOptions = useMemo(
    () => buildCoachProfileTagFilterOptions(offerings),
    [offerings],
  )

  useEffect(() => {
    if (serviceId === 'all') return
    if (!availableServices.some((service) => service.id === serviceId)) {
      setServiceId('all')
    }
  }, [availableServices, serviceId])

  useEffect(() => {
    const validIds = new Set(facilityOptions.map((facility) => facility.id))
    setFacilityKeys((previous) => {
      const next = previous.filter((key) => validIds.has(key))
      return next.length === previous.length ? previous : next
    })
  }, [facilityOptions])

  useEffect(() => {
    const validIds = new Set(tagOptions.map((tag) => tag.id))
    setTagKeys((previous) => {
      const next = previous.filter((key) => validIds.has(key))
      return next.length === previous.length ? previous : next
    })
  }, [tagOptions])

  const showLocationMenu = facilityOptions.length > 1
  const showTagMenu = tagOptions.length > 1
  const filterControlCount = availableServices.length
    + (showTagMenu ? 1 : 0)
    + (showLocationMenu ? 1 : 0)
  const showFilterToolbar = offerings.length > 0
    && !(filterControlCount === 1
      && availableServices.length === 1
      && !showTagMenu
      && !showLocationMenu)
  const hasActiveFilters = serviceId !== 'all'
    || facilityKeys.length > 0
    || tagKeys.length > 0

  const filtered = useMemo(() => {
    const next = offerings.filter((offering) => (
      offeringMatchesShopFilter(offering, serviceId)
      && offeringMatchesFacilityFilter(offering, facilityKeys)
      && offeringMatchesTagFilter(offering, tagKeys)
    ))
    return sortCoachProfileOfferingsByAvailability(next)
  }, [facilityKeys, offerings, serviceId, tagKeys])

  useEffect(() => {
    if (hasActiveFilters && filtered.length === 0) {
      setLocationAnchorEl(null)
      setTagAnchorEl(null)
    }
  }, [filtered.length, hasActiveFilters])

  const trackFilterChange = (filterName, filterValue, selected) => {
    trackCoachProfileEvent('change-coach-profile-offerings-filter', {
      filter_name: filterName,
      filter_value: filterValue,
      selected,
    })
  }

  const toggleServiceId = (nextId) => {
    trackFilterChange('service', nextId, serviceId !== nextId)
    setServiceId((current) => (current === nextId ? 'all' : nextId))
  }

  const toggleFacilityKey = (key) => {
    trackFilterChange('location', key, !facilityKeys.includes(key))
    setFacilityKeys((previous) => (
      previous.includes(key)
        ? previous.filter((item) => item !== key)
        : [...previous, key]
    ))
  }

  const toggleTagKey = (key) => {
    trackFilterChange('tag', key, !tagKeys.includes(key))
    setTagKeys((previous) => (
      previous.includes(key)
        ? previous.filter((item) => item !== key)
        : [...previous, key]
    ))
  }

  const closeFilterMenus = () => {
    setLocationAnchorEl(null)
    setTagAnchorEl(null)
  }

  const clearFilters = (position) => {
    trackCoachProfileEvent('click-coach-profile-offerings-clear-filters', { position })
    setServiceId('all')
    setFacilityKeys([])
    setTagKeys([])
    closeFilterMenus()
  }

  const sections = useMemo(() => {
    if (serviceId !== 'all') {
      const service = availableServices.find((item) => item.id === serviceId)
      return service && filtered.length
        ? [{ ...service, items: filtered }]
        : []
    }
    return COACH_PROFILE_OFFERING_SERVICES
      .map((service) => ({
        ...service,
        items: filtered.filter((offering) => (
          getCoachProfileOfferingServiceId(offering) === service.id
        )),
      }))
      .filter((section) => section.items.length > 0)
  }, [availableServices, filtered, serviceId])

  const hasExternalBooking = Boolean(externalBookingUrl)
  const hasPgaOfferings = offerings.length > 0

  if (hasExternalBooking && !hasPgaOfferings) {
    const headingId = id ? `${id}-heading` : 'coach-profile-offerings-heading'
    return (
      <Box
        className={`${classes.root} ${classes.rootBand}`}
        component="section"
        id={id}
        data-testid="coach-profile-offerings-shop"
        aria-labelledby={headingId}
      >
        <Typography variant="h5" component="h2" className={classes.heading} id={headingId}>
          {OFFERINGS_SHOP_HEADING}
        </Typography>
        <ExternalBookingCard externalBookingUrl={externalBookingUrl} />
        {loading ? (
          <Typography className={classes.loading}>Loading lessons and programs…</Typography>
        ) : null}
      </Box>
    )
  }

  if (loading && !availableServices.length && !hasExternalBooking) {
    return (
      <Box
        className={`${classes.root} ${classes.rootBand}`}
        id={id}
        data-testid="coach-profile-offerings-shop"
      >
        <Typography variant="h5" component="h2" className={classes.heading}>
          {OFFERINGS_SHOP_HEADING}
        </Typography>
        <Typography className={classes.loading}>Loading lessons and programs…</Typography>
      </Box>
    )
  }

  if (!availableServices.length) {
    return (
      <Box
        className={classes.root}
        id={id}
        data-testid="coach-profile-offerings-shop"
        aria-hidden="true"
      />
    )
  }

  const headingId = id ? `${id}-heading` : 'coach-profile-offerings-heading'
  const hideSectionHeading = sections.length === 1

  return (
    <Box
      className={`${classes.root} ${classes.rootBand}`}
      component="section"
      id={id}
      data-testid="coach-profile-offerings-shop"
      aria-labelledby={headingId}
    >
      <Typography variant="h5" component="h2" className={classes.heading} id={headingId}>
        {OFFERINGS_SHOP_HEADING}
      </Typography>

      {hasExternalBooking ? (
        <ExternalBookingCard
          externalBookingUrl={externalBookingUrl}
          className={classes.externalBannerBelowHeading}
        />
      ) : null}

      {showFilterToolbar && (
        <Box
          className={classes.filterToolbar}
          role="group"
          aria-label="Services filters"
          data-testid="coach-profile-shop-filters"
        >
          {availableServices.map((service) => {
            const Icon = service.iconKey
              ? COACH_PROFILE_OFFERING_FORMAT_ICON_COMPONENTS[service.iconKey]
              : null
            const selected = serviceId === service.id
            return (
              <Button
                key={service.id}
                type="button"
                variant="outlined"
                className={`${classes.filterPillButton} ${selected ? classes.filterPillButtonActive : ''}`}
                onClick={() => toggleServiceId(service.id)}
                aria-pressed={selected}
                startIcon={Icon ? (
                  <Icon
                    {...COACH_PROFILE_FILTER_ICON_PROPS}
                    data-testid={`shop-filter-icon-${service.id}`}
                  />
                ) : null}
              >
                {service.label}
              </Button>
            )
          })}

          {showTagMenu && (
            <>
              <Button
                type="button"
                variant="outlined"
                aria-label="Tags"
                aria-haspopup="true"
                aria-expanded={Boolean(tagAnchorEl)}
                className={`${classes.filterPillButton} ${tagKeys.length ? classes.filterPillButtonActive : ''}`}
                startIcon={<Tag {...COACH_PROFILE_FILTER_ICON_PROPS} />}
                endIcon={<ChevronDown {...COACH_PROFILE_FILTER_ICON_PROPS} />}
                onClick={(event) => {
                  closeFilterMenus()
                  setTagAnchorEl(event.currentTarget)
                }}
              >
                Tags
              </Button>
              <Popover
                open={Boolean(tagAnchorEl)}
                anchorEl={tagAnchorEl}
                onClose={() => setTagAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: classes.filterMenuPaper }}
              >
                <MenuList aria-label="Tags" aria-multiselectable="true">
                  {tagOptions.map((tag) => (
                    <FilterMenuOption
                      key={tag.id}
                      label={formatCoachProfileTagFilterOptionLabel(tag)}
                      selected={tagKeys.includes(tag.id)}
                      onSelect={() => toggleTagKey(tag.id)}
                    />
                  ))}
                </MenuList>
              </Popover>
            </>
          )}

          {showLocationMenu && (
            <>
              <Button
                type="button"
                variant="outlined"
                aria-label="Location"
                aria-haspopup="true"
                aria-expanded={Boolean(locationAnchorEl)}
                className={`${classes.filterPillButton} ${facilityKeys.length ? classes.filterPillButtonActive : ''}`}
                startIcon={<MapPin {...COACH_PROFILE_FILTER_ICON_PROPS} />}
                endIcon={<ChevronDown {...COACH_PROFILE_FILTER_ICON_PROPS} />}
                onClick={(event) => {
                  closeFilterMenus()
                  setLocationAnchorEl(event.currentTarget)
                }}
              >
                Location
              </Button>
              <Popover
                open={Boolean(locationAnchorEl)}
                anchorEl={locationAnchorEl}
                onClose={() => setLocationAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: classes.filterMenuPaper }}
              >
                <MenuList aria-label="Location" aria-multiselectable="true">
                  {facilityOptions.map((facility) => (
                    <FilterMenuOption
                      key={facility.id}
                      label={formatCoachProfileFacilityFilterOptionLabel(facility)}
                      selected={facilityKeys.includes(facility.id)}
                      onSelect={() => toggleFacilityKey(facility.id)}
                    />
                  ))}
                </MenuList>
              </Popover>
            </>
          )}

          <Tooltip title={hasActiveFilters ? 'Clear filters' : 'No filters to clear'}>
            <span className={classes.clearButtonWrap}>
              <Button
                type="button"
                variant="text"
                disableRipple
                aria-label="Clear filters"
                disabled={!hasActiveFilters}
                className={`${classes.iconFilterCircle} ${hasActiveFilters ? classes.iconFilterCircleActive : ''}`}
                startIcon={<X {...COACH_PROFILE_FILTER_ICON_PROPS} />}
                onClick={() => clearFilters('toolbar')}
              />
            </span>
          </Tooltip>
        </Box>
      )}

      {hasActiveFilters && filtered.length === 0 ? (
        <Box
          className={classes.filterEmpty}
          role="status"
          data-testid="coach-profile-shop-empty"
        >
          <Typography
            component="h3"
            variant="subtitle1"
            className={classes.filterEmptyTitle}
          >
            {FILTER_EMPTY_HEADING}
          </Typography>
          <Typography component="p" variant="body2" className={classes.filterEmptyBody}>
            {FILTER_EMPTY_BODY}
          </Typography>
          <Button
            type="button"
            variant="outlined"
            className={classes.filterEmptyCta}
            onClick={() => clearFilters('empty_state')}
          >
            {FILTER_EMPTY_CTA}
          </Button>
        </Box>
      ) : (
        sections.map((section) => (
          <OfferingSection
            key={section.id}
            sectionKey={section.id}
            label={section.label}
            offerings={section.items}
            hideHeading={hideSectionHeading}
          />
        ))
      )}
    </Box>
  )
}

CoachProfileOfferingsShop.propTypes = {
  /** Normalized offerings (private, group, or program). */
  offerings: PropTypes.array,
  /** Shows loading copy before offerings resolve. */
  loading: PropTypes.bool,
  /** Optional DOM id for in-page scroll targets (View Services). */
  id: PropTypes.string,
  /**
   * Coach Tools External Tool booking URL. When set, the shop shows an
   * External Booking Site card above PGA Coach listing rows when both exist.
   * Omit or null when missing.
   */
  externalBookingUrl: PropTypes.string,
}

CoachProfileOfferingsShop.defaultProps = {
  offerings: [],
  loading: false,
  id: undefined,
  externalBookingUrl: null,
}

export default CoachProfileOfferingsShop
